import { useEffect, useState } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical, Plus, X } from 'lucide-react';
import { ROLLE } from './api';

/* Ein Tag (Einheit) in einer Woche: je Rolle ein Block als Ablagefläche. Mainlift, Power 1 und Power 2 tragen eine
   Übung, Zubringer mehrere (Reihenfolge per Drag & Drop), Conditioning einen Inhalt. Die Felder speichern beim
   Verlassen. Welche Wochen eine Änderung trifft, steht oben im Editor („gilt für“). */

function Feld({ label, wert, darf, onSpeichern, breit, mehrzeilig, typ = 'text', testId }) {
  const [v, setV] = useState(wert ?? '');
  useEffect(() => { setV(wert ?? ''); }, [wert]);
  const fertig = () => { if (String(v) !== String(wert ?? '')) onSpeichern(typ === 'number' ? (v === '' ? null : Number(v)) : v); };
  const Tag = mehrzeilig ? 'textarea' : 'input';
  return (
    <label className={`flex flex-col gap-0.5 ${breit ? 'col-span-2 sm:col-span-4' : ''}`}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <Tag aria-label={label} value={v} disabled={!darf} type={mehrzeilig ? undefined : typ} rows={mehrzeilig ? 2 : undefined}
        onChange={(e) => setV(e.target.value)} onBlur={fertig} data-testid={testId}
        onKeyDown={(e) => { if (e.key === 'Enter' && !mehrzeilig) e.currentTarget.blur(); }}
        className="min-h-[44px] rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm disabled:opacity-70" />
    </label>
  );
}

function KraftFelder({ b, darf, onFeld, label }) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Feld label="Sätze" wert={b.saetze} darf={darf} onSpeichern={(v) => onFeld({ saetze: v })} testId={`feld-${label}-saetze`} />
      <Feld label="Wdh" wert={b.wdh} darf={darf} onSpeichern={(v) => onFeld({ wdh: v })} />
      {'intensitaet' in b && <Feld label="Intensität" wert={b.intensitaet} darf={darf} onSpeichern={(v) => onFeld({ intensitaet: v })} />}
      {'rpe' in b && <Feld label="RPE" wert={b.rpe} darf={darf} onSpeichern={(v) => onFeld({ rpe: v })} />}
      {'last' in b && <Feld label="Gewicht" wert={b.last} darf={darf} onSpeichern={(v) => onFeld({ last: v })} />}
      {'tempo' in b && <Feld label="Tempo" wert={b.tempo} darf={darf} onSpeichern={(v) => onFeld({ tempo: v })} />}
      {'dauer_min' in b && !('saetze' in b) && <Feld label="Minuten" typ="number" wert={b.dauer_min} darf={darf} onSpeichern={(v) => onFeld({ dauer_min: v })} />}
      {'hinweis' in b && <Feld breit label="Hinweis für den Kunden" wert={b.hinweis} darf={darf} onSpeichern={(v) => onFeld({ hinweis: v })} />}
      <Feld breit label="Notiz (nur Trainer)" wert={b.notiz} darf={darf} onSpeichern={(v) => onFeld({ notiz: v })} />
    </div>
  );
}

function Uebung({ id, index, name, darf, onEntfernen }) {
  return (
    <Draggable draggableId={id} index={index} isDragDisabled={!darf}>
      {(p, snap) => (
        <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
          className={`flex min-h-[44px] items-center gap-2 rounded-[10px] border px-3 py-1 text-sm ${snap.isDragging ? 'border-marke bg-card shadow-lg' : 'border-border bg-background'}`}>
          <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate font-semibold">{name}</span>
          {darf && (
            <button type="button" onClick={onEntfernen} aria-label={`${name} entfernen`}
              className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></button>
          )}
        </div>
      )}
    </Draggable>
  );
}

export default function TagEditor({ plan, woche, tagKey, darf, gewaehlt, onFeld, onEinfuegen, onEntfernen }) {
  const w = plan.wochen[woche - 1];
  const bloecke = w?.einheiten?.[tagKey];
  const typ = plan.tagestypen?.[tagKey] || 'training';
  if (!bloecke) return null;
  if (typ === 'restday') {
    return <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="plan-restday">Restday — an diesem Tag ist nichts geplant. Der Kunde sieht „Ruhetag“.</p>;
  }
  return (
    <div className="space-y-3" data-testid={`plan-tag-${tagKey}`}>
      {['haupt', 'zubringer', 'power1', 'power2', 'conditioning'].map((slot) => {
        const b = bloecke[slot] || {};
        const label = plan.labels?.[slot] || slot;
        const namen = slot === 'zubringer' ? (b.uebungen || []) : (b.uebung ? [b.uebung] : []);
        const leer = slot !== 'zubringer' && slot !== 'conditioning' && !namen.length;
        const fix = (plan.rhythmus || {})[slot];
        return (
          <section key={slot} className="rounded-[10px] border border-border bg-muted/40 p-3" aria-label={label}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-bold">{label} <span className="ml-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-bold text-muted-foreground">{ROLLE[slot]}</span></p>
              <span className="text-[11px] text-muted-foreground">{fix === 'fix' ? 'Übung für den ganzen Plan' : fix === 'phase' ? 'Übung je Phase' : 'je Woche'}</span>
            </div>
            {slot === 'conditioning' ? (
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <label className="flex flex-col gap-0.5">
                  <span className="text-[11px] text-muted-foreground">Format</span>
                  <select aria-label="Format" disabled={!darf} value={b.format || ''} onChange={(e) => onFeld(slot, { format: e.target.value })}
                    className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
                    {(plan.formate || []).map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </label>
                <Feld label="Minuten" typ="number" wert={b.dauer_min} darf={darf} onSpeichern={(v) => onFeld(slot, { dauer_min: v })} />
                <Feld breit mehrzeilig label="Inhalt" wert={b.inhalt} darf={darf} onSpeichern={(v) => onFeld(slot, { inhalt: v })} testId="feld-conditioning-inhalt" />
                <Feld breit label="Hinweis für den Kunden" wert={b.hinweis} darf={darf} onSpeichern={(v) => onFeld(slot, { hinweis: v })} />
              </div>
            ) : (
              <>
                <Droppable droppableId={`slot:${slot}`} isDropDisabled={!darf}>
                  {(prov, snap) => (
                    <div ref={prov.innerRef} {...prov.droppableProps}
                      className={`mt-2 min-h-[48px] space-y-1 rounded-md border border-dashed p-1 ${snap.isDraggingOver ? 'border-marke bg-marke/5' : 'border-border'}`}>
                      {namen.map((n, i) => (
                        <Uebung key={`${slot}-${n}`} id={`p:${slot}:${n}`} index={i} name={n} darf={darf} onEntfernen={() => onEntfernen(slot, n)} />
                      ))}
                      {prov.placeholder}
                      {!namen.length && <p className="px-2 py-2 text-xs text-muted-foreground">{darf ? 'Übung hierher ziehen' : 'Keine Übung'}</p>}
                    </div>
                  )}
                </Droppable>
                {darf && gewaehlt && (leer || slot === 'zubringer' || slot.startsWith('power') || slot === 'haupt') && (
                  <button type="button" onClick={() => onEinfuegen(slot)} data-testid={`einfuegen-${slot}`}
                    className="mt-1 inline-flex min-h-[44px] items-center gap-1 rounded-md px-2 text-xs font-semibold text-marke hover:bg-marke/10">
                    <Plus className="h-3.5 w-3.5" /> „{gewaehlt}“ hier einfügen
                  </button>
                )}
                <KraftFelder b={b} darf={darf} label={slot} onFeld={(werte) => onFeld(slot, werte)} />
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}
